import { Tag } from 'antd';
import type { PropsWithChildren } from 'react';

type Color =
  | 'blue'
  | 'green'
  | 'red'
  | 'yellow'
  | 'purple'
  | 'cyan'
  | 'magenta'
  | 'default'
  | 'transparent'
  | 'orange';

/**
 * Props for the SkyLabeledBlock component
 */
export interface SkyLabeledBlockProps extends PropsWithChildren {
  /**
   * Optional CSS class name to apply to the container
   */
  className?: string;

  /**
   * Title text to display in the bottom-centered tag
   */
  title?: string;

  /**
   * Color theme for the block border and tag
   * @default 'blue'
   */
  color?: Color;
}

const colorMap: Record<Color, string> = {
  default: '',
  blue: 'bg-blue-500/10 border-blue-500',
  green: 'bg-green-500/10 border-green-500',
  red: 'bg-red-500/10 border-red-500',
  yellow: 'bg-yellow-500/10 border-yellow-500',
  purple: 'bg-purple-500/10 border-purple-500',
  cyan: 'bg-cyan-500/10 border-cyan-500',
  magenta: 'bg-pink-500/10 border-pink-500',
  transparent: 'border-transparent',
  orange: 'bg-orange-400/10 border-orange-400',
};

/**
 * SkyLabeledBlock component wraps content in a colored border with an optional bottom-centered tag label.
 *
 * This component is useful for visually grouping related content with:
 * - Colored border and background matching the theme
 * - Optional tag label positioned at the bottom center
 * - Flexible content rendering
 *
 * @example
 * ```tsx
 * // Basic usage with blue theme
 * <SkyLabeledBlock title="Liquidity" color="blue">
 *   <div>Your content here</div>
 * </SkyLabeledBlock>
 *
 * // With custom styling
 * <SkyLabeledBlock
 *   title="Trigger"
 *   color="green"
 *   className="xl:ml-auto xl:max-w-60"
 * >
 *   <SkyDepthItem liquidity={data} type="our" />
 * </SkyLabeledBlock>
 * ```
 */
export const SkyLabeledBlock = ({
  children,
  className = '',
  title = '',
  color = 'blue',
}: SkyLabeledBlockProps) => {
  return (
    <div
      data-testid="sky-labeled-block"
      className={`relative w-full rounded-md border ${colorMap[color]} px-2 ${title ? `pb-2` : ''} sm:-mt-px ${className}`}
    >
      {children}
      {title && (
        <Tag
          variant="solid"
          color={color}
          className="absolute -bottom-2 left-1/2 z-10 -translate-x-1/2 text-[10px] leading-relaxed font-bold tracking-wider uppercase"
        >
          {title}
        </Tag>
      )}
    </div>
  );
};

export default SkyLabeledBlock;
